<script setup lang="ts">
  /**
   * Toast — 03-components/11-toasts · OSCloud DS 1.1.166
   *
   * One transient message. `BfToastViewport` owns the stack, the placement and the
   * enter/leave transitions; this component is only the box.
   *
   * Two deliberate deviations from the brand platform's radix `ToastRoot`, both the design
   * system's own: the close button is always visible rather than hover-revealed, and a
   * countdown ring shows the time remaining. The ring's dash animation *is* the timer —
   * dismissal fires on its `animationend`, so hovering pauses the countdown and the dismissal
   * together.
   */
  import { computed, onBeforeUnmount, onMounted, ref } from 'vue'

  type BfToastType = 'neutral' | 'info' | 'success' | 'warning' | 'error'

  const props = withDefaults(
    defineProps<{
      type?: BfToastType
      /** The sentence. Rendered as body text on its own; promoted to the headline by `description`. */
      message: string
      /** The detail under a headline. Omit for the one-sentence form, which is the common one. */
      description?: string
      /** Auto-dismiss delay in ms. The card's own value. */
      duration?: number
    }>(),
    { type: 'neutral', description: '', duration: 4500 },
  )

  const emit = defineEmits<{ close: [] }>()

  /** Lucide glyph paths, one entry per variant — `TOAST_ICONS` in the card's script. */
  const ICONS: Record<BfToastType, string[]> = {
    neutral: ['M12 2a10 10 0 1 0 0 20 10 10 0 1 0 0-20', 'M12 16v-4', 'M12 8h.01'],
    info: ['M12 2a10 10 0 1 0 0 20 10 10 0 1 0 0-20', 'M12 16v-4', 'M12 8h.01'],
    success: ['M22 11.08V12a10 10 0 1 1-5.93-9.14', 'm9 11 3 3L22 4'],
    warning: ['m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3', 'M12 9v4', 'M12 17h.01'],
    error: ['M12 2a10 10 0 1 0 0 20 10 10 0 1 0 0-20', 'm15 9-6 6', 'm9 9 6 6'],
  }

  const iconPaths = computed(() => ICONS[props.type])

  /**
   * The prop keeps the vocabulary every other alert in the app uses (`BaseNotification`,
   * `BfNotice`); the rendered modifier is the design system's, which names the informational
   * tone in full.
   */
  const MODIFIER_BY_TYPE: Record<BfToastType, string> = {
    neutral: 'neutral',
    info: 'information',
    success: 'success',
    warning: 'warning',
    error: 'error',
  }

  const typeModifier = computed(() => MODIFIER_BY_TYPE[props.type])

  /** An error interrupts; the rest are progress reports. */
  const role = computed(() => (props.type === 'error' ? 'alert' : 'status'))

  const reducedMotion = ref(false)
  let fallbackTimer: ReturnType<typeof setTimeout> | undefined

  onMounted(() => {
    reducedMotion.value = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    // With the ring animation suppressed there is no animationend to wait on.
    if (reducedMotion.value) fallbackTimer = setTimeout(() => emit('close'), props.duration)
  })

  onBeforeUnmount(() => clearTimeout(fallbackTimer))

  /**
   * Scoped keyframes are rewritten to `toast-countdown-<hash>`, so match on the prefix rather
   * than the exact name.
   */
  const onCountdownEnd = (event: AnimationEvent) => {
    if (event.animationName.startsWith('toast-countdown')) emit('close')
  }
</script>

<template>
  <div class="toast" :class="`toast--${typeModifier}`" :role="role">
    <svg
      class="toast__icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
    >
      <path v-for="d in iconPaths" :key="d" :d="d" />
    </svg>

    <div class="toast__body">
      <!-- A one-sentence toast is BODY text, not a heading: __title is display semi-bold and
           reads as a title with nothing under it. The pair is for a toast that genuinely has a
           headline and a detail. -->
      <template v-if="description">
        <p class="toast__title">{{ message }}</p>
        <p class="toast__desc">{{ description }}</p>
      </template>
      <p v-else class="toast__desc">{{ message }}</p>
    </div>

    <button class="toast__close" type="button" aria-label="Close" @click="emit('close')">
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        aria-hidden="true"
      >
        <path d="M18 6 6 18" />
        <path d="m6 6 12 12" />
      </svg>
    </button>

    <svg class="toast__timer" viewBox="0 0 24 24" aria-hidden="true">
      <circle class="toast__timer-track" cx="12" cy="12" r="10" />
      <circle
        class="toast__timer-bar"
        cx="12"
        cy="12"
        r="10"
        :style="{ animationDuration: `${duration}ms` }"
        @animationend="onCountdownEnd"
      />
    </svg>
  </div>
</template>

<style scoped lang="scss">
  .toast {
    position: relative;
    display: flex;
    align-items: flex-start;
    gap: 12px;
    width: 100%;
    overflow: hidden;
    border: 1px solid transparent;
    border-radius: var(--bf-r-sm); /* the card's calc(--control-radius - 4px) — 4px */
    /* 48px trailing (DS 1.1.130): the close glyph and the timer both occupy the band 12–26px
       from the edge, so the text column stops 22px short of them. The old 32px landed the last
       character 6px from the glyphs on any content-sized toast — which the application stack
       always is, since it sizes every toast to its own longest line. */
    padding: 16px 48px 16px 16px;
    /* Kept as the card writes it: the elevation ladder in --bf-shadow-* is the page's, and a
       floating toast is the one surface the design system gives Tailwind's shadow-lg. */
    box-shadow:
      0 10px 15px -3px rgba(0, 0, 0, 0.1),
      0 4px 6px -4px rgba(0, 0, 0, 0.1);

    /* 24px, centred on the toast's height (DS 1.1.164) — with a title, without one, or beside a
       description that wraps. */
    &__icon {
      width: 24px;
      height: 24px;
      align-self: center;
      flex-shrink: 0;
    }

    /* Floor = one title line (18px) + the 4px gap + one description line (14 × 1.4286), so the
       one-sentence toast is exactly as tall as a one-line titled one (76px) and its sentence centres in it. */
    &__body {
      flex: 1;
      min-width: 0;
      min-height: calc(18px + 4px + 14px * 1.4286);
      display: flex;
      flex-direction: column;
      justify-content: center;
      gap: 4px;
    }

    &__title {
      margin: 0;
      color: inherit;
      font-family: var(--bf-font-display);
      font-size: var(--bf-fs-sm);
      font-weight: var(--bf-fw-semibold);
      line-height: 18px; /* was 1.25 (17.5px): a whole-pixel toast height */
    }

    &__desc {
      margin: 0;
      font-family: var(--bf-font-body);
      font-size: var(--bf-fs-sm);
      line-height: 1.4286;
      opacity: 0.9;
    }

    /* Always visible: a toast that self-dismisses should never make the escape hatch
       hunt-to-find. */
    &__close {
      position: absolute;
      top: 8px;
      right: 8px;
      display: inline-flex;
      padding: 4px;
      border: 0;
      border-radius: var(--bf-r-sm);
      background: transparent;
      color: currentcolor;
      opacity: 0.6;
      cursor: pointer;
      transition: opacity var(--bf-dur-fast) var(--bf-ease);

      &:hover {
        opacity: 1;
      }

      &:focus-visible {
        opacity: 1;
        outline: none;
        box-shadow: 0 0 0 1px currentcolor;
      }

      svg {
        width: 14px;
        height: 14px;
      }
    }

    /* 12px inset matches the close glyph, which sits 8px out plus its own 4px of padding. */
    &__timer {
      position: absolute;
      right: 12px;
      bottom: 12px;
      width: 14px;
      height: 14px;
      transform: rotate(-90deg); /* start the sweep at 12 o'clock */
    }

    &__timer-track,
    &__timer-bar {
      fill: none;
      stroke-width: 2;
    }

    &__timer-track {
      stroke: currentcolor;
      opacity: 0.2;
    }

    &__timer-bar {
      stroke: currentcolor;
      opacity: 0.7;
      stroke-linecap: round;
      stroke-dasharray: 62.83; /* 2πr, r = 10 */
      animation: toast-countdown 4500ms linear forwards;
    }

    &:hover &__timer-bar {
      animation-play-state: paused;
    }

    /* ---------- variants ---------- */
    &--neutral {
      border-color: var(--bf-line);
      background: var(--bf-bg);
      color: var(--bf-fg-1);
    }

    /* Status variants read the palette only (DS 1.1.165): a pale surface, its -mid hairline,
       --bf-dark-blue text, and the family's deepest step on the icon and the title. Every
       surface is opaque — a toast floats over page content. */
    &--information {
      border-color: var(--bf-navy-mid);
      background: var(--bf-navy-light);
      color: var(--bf-dark-blue);
    }

    &--success {
      border-color: var(--bf-green-mid);
      background: var(--bf-green-light);
      color: var(--bf-dark-blue);
    }

    &--warning {
      border-color: var(--bf-orange-mid);
      background: var(--bf-orange-ultralight);
      color: var(--bf-dark-blue);
    }

    &--information &__icon,
    &--information &__title {
      color: var(--bf-navy);
    }

    &--success &__icon,
    &--success &__title {
      color: var(--bf-green-deep);
    }

    &--warning &__icon,
    &--warning &__title {
      color: var(--bf-orange-deep);
    }

    /* The only variant with a solid fill — white on the red. The card reads --red, which is
       #d33b3b inside the DS; the app's --bf-red is still #ff0000 (the open divergence), so the
       toast takes --bf-error-accent, which holds the DS value. Icon and title inherit the white. */
    &--error {
      border-color: var(--bf-error-accent);
      background: var(--bf-error-accent);
      color: var(--bf-white);
    }
  }

  @keyframes toast-countdown {
    to {
      stroke-dashoffset: 62.83;
    }
  }

  /* The countdown falls back to a plain timer in the script. */
  @media (prefers-reduced-motion: reduce) {
    .toast__timer-bar {
      animation: none;
    }
  }
</style>
